import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DangerAlert, WarningAlert } from "@/components/shared/Alerts";
import { useStructure } from "@/hooks/useDesignXML";
import { usePermissionContext } from "@/context/PermissionContext";
import type { OpticalPort, PortCombo, ProbePad } from "@/types/designs";
import type { StructureSummary } from "@/types/structure";
import type { BreakingChangesDetail } from "../DesignUploadStep.types";
import type { StructureUpdatePayload, StructureUpdatePreview } from "@/types/structure";
import { usePreviewStructureUpdate, useUpdateStructure } from "@/hooks/useStructures";
import { parseSelections, stringifySelections } from "../DesignUploadStep.functions";

import { BreakingChangesDialog } from "./dialogs/BreakingChangesDialog";
import { StructureUpdatePreviewDialog } from "./dialogs/StructureUpdatePreviewDialog";
import { OpticalPortsTable } from "./tables/OpticalPortsTable";
import { PortCombosTable } from "./tables/PortCombosTable";
import { ProbePadsTable } from "./tables/ProbePadsTable";
import { Button } from "@/components/ui/button";

const overviewTabs = [
  { value: "port-combos", label: "Port Combos" },
  { value: "optical-ports", label: "Optical Ports" },
  { value: "probe-pads", label: "Probe Pads" },
] as const;

type OverviewTabValue = (typeof overviewTabs)[number]["value"];

interface DesignOverviewFormProps {
  onOpenChange: (value: boolean) => void;
  open: boolean;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  selectedStructure: StructureSummary | null;
}

export function DesignOverviewForm({
  open,
  onOpenChange,
  isEditing,
  setIsEditing,
  selectedStructure,
}: DesignOverviewFormProps) {
  const { hasPermission } = usePermissionContext();
  const initializedStructureIdRef = useRef<string | null>(null);
  const [activeTab, setActiveTab] = useState<OverviewTabValue>("port-combos");
  const [deviceClassDraft, setDeviceClassDraft] = useState("");
  const [isReferenceWaveguideDraft, setIsReferenceWaveguideDraft] = useState(false);
  const [opticalPortsDraft, setOpticalPortsDraft] = useState<OpticalPort[]>([]);
  const [portCombosDraft, setPortCombosDraft] = useState<PortCombo[]>([]);
  const [probePadsDraft, setProbePadsDraft] = useState<ProbePad[]>([]);
  const [previewData, setPreviewData] = useState<StructureUpdatePreview | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showBreakingChangesDialog, setShowBreakingChangesDialog] = useState(false);
  const [breakingChanges, setBreakingChanges] = useState<BreakingChangesDetail | null>(null);

  const {
    data: structureOverview,
    mutate: refreshStructure,
    error: structureOverviewError,
    isLoading: isStructureOverviewLoading,
  } = useStructure(selectedStructure?.id);
  const { trigger: previewUpdate } = usePreviewStructureUpdate();
  const { trigger: applyUpdate, isMutating: isApplyingUpdate } = useUpdateStructure();

  useEffect(() => {
    if (!open || !selectedStructure?.id || !structureOverview) {
      if (!open) {
        initializedStructureIdRef.current = null;
        setIsEditing(false);
      }
      return;
    }

    if (initializedStructureIdRef.current === selectedStructure.id) {
      return;
    }

    setActiveTab("port-combos");
    setDeviceClassDraft(structureOverview?.deviceClass ?? "");
    setIsReferenceWaveguideDraft(structureOverview?.isReferenceWaveguide ?? false);
    setOpticalPortsDraft(structureOverview?.opticalPorts ?? []);
    setPortCombosDraft(structureOverview?.portCombos ?? []);
    setProbePadsDraft(structureOverview?.probePads ?? []);
    setPreviewData(null);
    setShowPreviewDialog(false);
    setBreakingChanges(null);

    initializedStructureIdRef.current = selectedStructure.id;
  }, [open, selectedStructure?.id, setIsEditing, structureOverview]);

  const canEditStructure = hasPermission("structures", "update");

  const getDuplicateName = <T extends { name: string }>(items: T[]) => {
    const counts = new Map<string, number>();

    for (const item of items) {
      const trimmedName = item.name.trim();
      if (!trimmedName) continue;

      const key = trimmedName.toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);

      if ((counts.get(key) ?? 0) > 1) {
        return trimmedName;
      }
    }

    return null;
  };

  const updateSelectionNames = (value: string, renameMap: Map<string, string>) => {
    const selections = parseSelections(value).map((entry) => renameMap.get(entry) ?? entry);
    return stringifySelections(selections);
  };

  const getPortComboNameMaps = () => {
    const opticalPortRenameMap = new Map<string, string>();
    const probePadRenameMap = new Map<string, string>();

    for (const originalPort of structureOverview?.opticalPorts ?? []) {
      const currentPort = opticalPortsDraft.find((port) => port.id === originalPort.id);
      if (currentPort && originalPort.name !== currentPort.name) {
        opticalPortRenameMap.set(originalPort.name, currentPort.name);
      }
    }

    for (const originalPad of structureOverview?.probePads ?? []) {
      const currentPad = probePadsDraft.find((pad) => pad.id === originalPad.id);
      if (currentPad && originalPad.name !== currentPad.name) {
        probePadRenameMap.set(originalPad.name, currentPad.name);
      }
    }

    return { opticalPortRenameMap, probePadRenameMap };
  };

  const mapPortComboReferences = (
    combos: PortCombo[],
    opticalPortRenameMap: Map<string, string>,
    probePadRenameMap: Map<string, string>,
  ) =>
    combos.map((combo) => ({
      ...combo,
      inputs: updateSelectionNames(combo.inputs, opticalPortRenameMap),
      outputs: updateSelectionNames(combo.outputs, opticalPortRenameMap),
      pads: updateSelectionNames(combo.pads, probePadRenameMap),
    }));

  const mapOpticalPortsForUpdate = (ports: OpticalPort[]) =>
    ports.map((port) => ({
      id: port.id,
      name: port.name,
      x: port.x,
      y: port.y,
      orientation: port.orientation,
      cell_port: port.cellPort,
      tag: port.tag,
    }));

  const mapPortCombosForUpdate = (combos: PortCombo[]) =>
    combos.map((combo) => ({
      id: combo.id,
      name: combo.name,
      index: combo.index,
      settings: combo.settings,
      ref_wg: combo.refWg,
      blind_step: combo.blindStep,
      align_at_peak: combo.alignAtPeak,
      z_margin: combo.zMargin,
      align_pwr_rng: combo.alignPwrRng,
      measure_pwr_rng: combo.measurePwrRng,
      inputs: combo.inputs,
      outputs: combo.outputs,
      pads: combo.pads,
      dynamic_alignment: combo.dynamicAlignment,
      probe_check: combo.probeCheck,
    }));

  const mapProbePadsForUpdate = (pads: ProbePad[]) =>
    pads.map((pad) => ({
      id: pad.id,
      name: pad.name,
      x: pad.x,
      y: pad.y,
      orientation: pad.orientation,
    }));

  const handleCancelEdit = () => {
    setDeviceClassDraft(structureOverview?.deviceClass ?? "");
    setIsReferenceWaveguideDraft(structureOverview?.isReferenceWaveguide ?? false);
    setOpticalPortsDraft(structureOverview?.opticalPorts ?? []);
    setPortCombosDraft(structureOverview?.portCombos ?? []);
    setProbePadsDraft(structureOverview?.probePads ?? []);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedStructure) return;

    const duplicateOpticalPortName = getDuplicateName(opticalPortsDraft);
    if (duplicateOpticalPortName) {
      toast.error(
        `Optical port names must be unique. "${duplicateOpticalPortName}" is already present in the updated selection.`,
      );
      return;
    }

    const duplicateProbePadName = getDuplicateName(probePadsDraft);
    if (duplicateProbePadName) {
      toast.error(
        `Probe pad names must be unique. "${duplicateProbePadName}" is already present in the updated selection.`,
      );
      return;
    }

    const { opticalPortRenameMap, probePadRenameMap } = getPortComboNameMaps();
    const updatedPortCombos = mapPortComboReferences(
      portCombosDraft,
      opticalPortRenameMap,
      probePadRenameMap,
    );

    setPortCombosDraft(updatedPortCombos);

    const payload: StructureUpdatePayload = {
      device_class: deviceClassDraft,
      is_reference_waveguide: isReferenceWaveguideDraft,
      optical_ports: mapOpticalPortsForUpdate(opticalPortsDraft),
      port_combos: mapPortCombosForUpdate(updatedPortCombos),
      probe_pads: mapProbePadsForUpdate(probePadsDraft),
    };

    try {
      const preview = await previewUpdate({
        ...payload,
        id: selectedStructure.id,
      });

      setPreviewData(preview);
      setShowPreviewDialog(true);
    } catch (error: any) {
      const detail = error?.detail as BreakingChangesDetail | undefined;

      if (detail?.structures_removed?.length || detail?.structures_with_name_changes?.length) {
        setBreakingChanges(detail);
        setShowBreakingChangesDialog(true);
      }
    }
  };

  const handleApplyUpdate = async () => {
    if (!selectedStructure) return;

    const { opticalPortRenameMap, probePadRenameMap } = getPortComboNameMaps();
    const updatedPortCombos = mapPortComboReferences(
      portCombosDraft,
      opticalPortRenameMap,
      probePadRenameMap,
    );

    await applyUpdate({
      id: selectedStructure.id,
      device_class: deviceClassDraft,
      is_reference_waveguide: isReferenceWaveguideDraft,
      optical_ports: mapOpticalPortsForUpdate(opticalPortsDraft),
      port_combos: mapPortCombosForUpdate(updatedPortCombos),
      probe_pads: mapProbePadsForUpdate(probePadsDraft),
    });

    setShowPreviewDialog(false);
    setPreviewData(null);
    setIsEditing(false);
    await refreshStructure();
  };

  const structureName = structureOverview?.name ?? selectedStructure?.name ?? "Structure details";
  const testSiteName = structureOverview?.testSiteName ?? "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden mx-auto flex max-h-[85vh] w-[92vw] max-w-[92vw] flex-col gap-0 p-0 sm:w-[95vw] sm:max-w-[95vw] lg:w-[85vw] lg:max-w-[85vw]">
        {selectedStructure ? (
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as OverviewTabValue)}
            className="flex min-h-[85vh] gap-0 flex-col"
          >
            <div className="border-b bg-background px-6 pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                    Structure
                  </p>
                  <h2 className="truncate text-2xl font-semibold">{structureName}</h2>
                  <p className="text-sm truncate text-muted-foreground">
                    ID: {selectedStructure.id}
                  </p>
                </div>
                {canEditStructure && (
                  <div className="flex shrink-0 items-center gap-2 mr-3">
                    {isEditing ? (
                      <>
                        <Button type="button" onClick={() => void handleSaveEdit()}>
                          Save
                        </Button>
                        <Button type="button" variant="outline" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button type="button" variant="outline" onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 rounded-md border bg-muted/20 p-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Test Site Name
                  </p>
                  <p className="text-sm font-medium">{testSiteName}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Device Class
                  </p>
                  {isEditing ? (
                    <Input
                      value={deviceClassDraft}
                      onChange={(event) => setDeviceClassDraft(event.target.value)}
                      className="h-8"
                    />
                  ) : (
                    <p className="text-sm font-medium">{deviceClassDraft || "—"}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Is Reference Waveguide
                  </p>
                  {isEditing ? (
                    <label className="flex h-8 items-center gap-2 text-sm font-medium">
                      <Checkbox
                        checked={isReferenceWaveguideDraft}
                        onCheckedChange={(checked) =>
                          setIsReferenceWaveguideDraft(checked === true)
                        }
                      />
                      <span>{isReferenceWaveguideDraft ? "Yes" : "No"}</span>
                    </label>
                  ) : (
                    <p className="text-sm font-medium">
                      {isReferenceWaveguideDraft ? "Yes" : "No"}
                    </p>
                  )}
                </div>
              </div>

              <TabsList className="mt-4 w-full justify-start gap-2 rounded-md bg-muted/60 p-1">
                {overviewTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="min-w-[140px] rounded-sm px-3 py-2"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <div className="min-h-0 flex-1 overflow-auto px-6 pt-0 pb-1 [&_[data-slot=table-container]]:min-w-full [&_[data-slot=table-container]]:w-max [&_[data-slot=table-container]]:overflow-visible [&_[data-slot=table]]:min-w-full [&_[data-slot=table]]:w-max [&_[data-slot=table-head]]:sticky [&_[data-slot=table-head]]:top-0 [&_[data-slot=table-head]]:z-10 [&_[data-slot=table-head]]:bg-background">
              {structureOverviewError ? (
                <DangerAlert>{structureOverviewError.message}</DangerAlert>
              ) : isStructureOverviewLoading ? (
                <div className="p-2">Loading structure...</div>
              ) : structureOverview ? (
                <>
                  <TabsContent value="port-combos" className="mt-0">
                    <PortCombosTable
                      isEditing={isEditing}
                      draftCombos={portCombosDraft}
                      onDraftCombosChange={setPortCombosDraft}
                    />
                  </TabsContent>
                  <TabsContent value="optical-ports" className="mt-0">
                    <OpticalPortsTable
                      isEditing={isEditing}
                      draftPorts={opticalPortsDraft}
                      onDraftPortsChange={setOpticalPortsDraft}
                    />
                  </TabsContent>
                  <TabsContent value="probe-pads" className="mt-0">
                    <ProbePadsTable
                      isEditing={isEditing}
                      draftPads={probePadsDraft}
                      onDraftPadsChange={setProbePadsDraft}
                    />
                  </TabsContent>
                </>
              ) : (
                <WarningAlert>No structure data found.</WarningAlert>
              )}
            </div>
          </Tabs>
        ) : (
          <div className="flex min-h-[60vh] items-center justify-center p-6">
            <WarningAlert>No structure selected.</WarningAlert>
          </div>
        )}
      </DialogContent>

      <StructureUpdatePreviewDialog
        preview={previewData}
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        onApply={() => void handleApplyUpdate()}
        isApplying={isApplyingUpdate}
      />

      {breakingChanges && (
        <BreakingChangesDialog
          detail={breakingChanges}
          open={showBreakingChangesDialog}
          onOpenChange={setShowBreakingChangesDialog}
        />
      )}
    </Dialog>
  );
}
